import { PageTitle } from "components/PageTitle";
import { Button } from "components/ui/Button";
import { Link } from "components/ui/Link";
import { Loading } from "components/ui/Loading/Loading";
import { StatusBadge } from "components/ui/StatusBadge";
import { Table } from "components/ui/Table/Table";
import {
  type AdminTenant,
  useAdminTenantsApi,
  useDeleteAdminTenantApi,
  useUpdateAdminTenantApi,
} from "hooks/useAdminApi";
import { type FC, useState } from "react";
import { useNavigate } from "react-router-dom";

export const TenantsPage: FC = () => {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useAdminTenantsApi();
  const { updateTenant } = useUpdateAdminTenantApi();
  const { deleteTenant } = useDeleteAdminTenantApi();

  const [searchTerm, setSearchTerm] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const filteredTenants =
    data?.items?.filter(
      tenant =>
        tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (tenant.description || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()),
    ) || [];

  const handleToggleActive = async (tenant: AdminTenant) => {
    setActionLoading(true);
    try {
      await updateTenant(tenant.id, { isActive: !tenant.isActive });
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (tenantId: string) => {
    setActionLoading(true);
    try {
      await deleteTenant(tenantId);
      setDeleteConfirm(null);
      await refetch();
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>テナント管理</PageTitle>
        <div className="alert alert-error">
          <svg
            className="stroke-current shrink-0 h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>エラーが発生しました: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <PageTitle>テナント管理</PageTitle>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin")}
        >
          ← ダッシュボードに戻る
        </Button>
      </div>

      {/* 検索 */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="テナント名または説明で検索..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="input input-bordered input-sm w-full md:w-96"
        />
      </div>

      {/* テーブル */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <Table.Root density="compact" zebra stickyHeader>
          <Table.Head sticky>
            <tr>
              <Table.HeaderCell>テナント名</Table.HeaderCell>
              <Table.HeaderCell>説明</Table.HeaderCell>
              <Table.HeaderCell>ユーザー数</Table.HeaderCell>
              <Table.HeaderCell>ステータス</Table.HeaderCell>
              <Table.HeaderCell>作成日</Table.HeaderCell>
              <Table.HeaderCell align="right">操作</Table.HeaderCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-center py-8">
                  <Loading size="md" />
                </td>
              </tr>
            ) : filteredTenants.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-gray-500">
                  テナントが見つかりません
                </td>
              </tr>
            ) : (
              filteredTenants.map(tenant => (
                <Table.Row key={tenant.id}>
                  <Table.Cell>
                    <Link to={`/admin/tenants/${tenant.id}`}>
                      {tenant.name}
                    </Link>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-gray-600 line-clamp-2">
                      {tenant.description || "-"}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <StatusBadge tone="soft-neutral">
                      {tenant.userCount}
                    </StatusBadge>
                  </Table.Cell>
                  <Table.Cell>
                    <StatusBadge
                      tone={tenant.isActive ? "soft-success" : "soft-neutral"}
                    >
                      {tenant.isActive ? "有効" : "無効"}
                    </StatusBadge>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="text-gray-500">
                      {new Date(tenant.createdAt).toLocaleDateString("ja-JP")}
                    </span>
                  </Table.Cell>
                  <Table.Cell align="right">
                    <div className="flex justify-end gap-2">
                      <Button
                        onClick={() => handleToggleActive(tenant)}
                        disabled={actionLoading}
                        size="xs"
                        variant={tenant.isActive ? "warning" : "success"}
                      >
                        {tenant.isActive ? "無効化" : "有効化"}
                      </Button>
                      <Button
                        onClick={() => setDeleteConfirm(tenant.id)}
                        disabled={actionLoading}
                        variant="destructive"
                        size="xs"
                      >
                        削除
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </div>

      {/* 統計 */}
      <div className="mt-4 text-sm text-gray-600">
        全 {data?.total || 0} 件中 {filteredTenants.length} 件を表示
      </div>

      {/* 削除確認モーダル */}
      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg text-color-title">
              テナントを削除しますか？
            </h3>
            <p className="py-4 text-gray-600">
              この操作は取り消すことができません。テナントに関連する全てのデータが削除されます。
            </p>
            <div className="modal-action">
              <Button
                onClick={() => setDeleteConfirm(null)}
                disabled={actionLoading}
                variant="ghost"
              >
                キャンセル
              </Button>
              <Button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={actionLoading}
                variant="destructive"
              >
                {actionLoading ? <Loading size="sm" /> : "削除する"}
              </Button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => setDeleteConfirm(null)}
          ></div>
        </div>
      )}
    </div>
  );
};

export default TenantsPage;
